import React, { useState } from 'react';
import { ScanLine, Play, Loader2 } from 'lucide-react';
import { runSiteAudit } from '../../lib/runSiteAudit';

// "Run audit now" — full-site audit of the account's page map (page map is
// refreshed first, missing pages are skipped), driven in small steps by
// runSiteAudit. Disabled when the account has no site URL.

const STYLES = {
  pill: 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#B0FC38]/15 border border-[#B0FC38]/40 text-[12px] text-white hover:bg-[#B0FC38]/25 disabled:opacity-50 disabled:cursor-not-allowed',
  solid: 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] bg-[#B0FC38] hover:bg-[#9BE22E] text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed',
};

function summarize(result) {
  const parts = [];
  parts.push(result.score == null ? 'No pages audited' : result.worstScore != null && result.worstScore !== result.score ? `Site score ${result.score} (average; lowest page ${result.worstScore})` : `Site score ${result.score}`);
  parts.push(`${result.pagesAudited} page${result.pagesAudited === 1 ? '' : 's'} audited`);
  const pruned = result.pageMapRefresh?.pruned || 0;
  const skipped = result.notFound.length + pruned;
  if (skipped) parts.push(`${skipped} removed page${skipped === 1 ? '' : 's'} skipped`);
  const added = result.pageMapRefresh?.added || 0;
  if (added) parts.push(`${added} new page${added === 1 ? '' : 's'} found`);
  return parts.join(' · ');
}

export default function RunAuditButton({ account, onCompleted, variant = 'pill', className = '' }) {
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState('');

  const disabled = running || !account?.siteUrl;

  const runAudit = async () => {
    setError('');
    setSummary('');
    setProgress(null);
    setRunning(true);
    try {
      const result = await runSiteAudit(account.id, { onProgress: setProgress });
      setSummary(summarize(result));
      if (result.failed.length) {
        setError(`${result.failed.length} page${result.failed.length === 1 ? '' : 's'} could not be audited: ${result.failed.map((f) => f.path).join(', ')}`);
      }
      onCompleted?.(result);
    } catch (err) {
      setError(err?.data?.error || err?.message || 'Audit failed');
    } finally {
      setRunning(false);
    }
  };

  const Icon = variant === 'solid' ? Play : ScanLine;
  return (
    <div className={`inline-flex items-center gap-2 flex-wrap ${className}`}>
      <button
        type="button"
        data-testid="run-audit-button"
        disabled={disabled}
        onClick={runAudit}
        title={!account?.siteUrl ? 'Set a site URL first' : 'Audit every page of the site'}
        className={STYLES[variant] || STYLES.pill}
      >
        {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Icon className="w-3.5 h-3.5" />}
        {running ? 'Running…' : 'Run audit now'}
      </button>
      {running && (
        <div data-testid="run-audit-progress" role="status" className="text-[11px] text-[#B0FC38]">
          {progress ? `Auditing pages ${progress.done} of ${progress.total}…` : 'Checking the site’s pages…'}
        </div>
      )}
      {!running && summary && (
        <div data-testid="run-audit-summary" role="status" className="text-[11px] text-gray-400">{summary}</div>
      )}
      {error && (
        <div data-testid="run-audit-error" className="text-[11px] text-red-300/90">{error}</div>
      )}
    </div>
  );
}
